import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Activity, Archive, Check, CheckCircle2, ChevronDown, ChevronRight,
  ExternalLink, Inbox, RefreshCw, Settings2, ShieldCheck, Wrench, X,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const HUMAN_CATEGORIES = new Set([
  "anslag", "evenemang", "andringsforslag", "bilder", "kallbevakning_kanslig",
]);

const STATUS = {
  ok: { dot: "bg-pine", text: "OK" },
  warning: { dot: "bg-amber-500", text: "Kontrollera" },
  stale: { dot: "bg-amber-500", text: "Inaktuellt" },
  error: { dot: "bg-falu", text: "Fel" },
  unknown: { dot: "bg-bark/40", text: "Okänt" },
};

function issueLabel(item) {
  const blockers = new Set(item.review_blockers || []);
  if (blockers.has("contradiction")) return "Uppgifter krockar med befintlig information";
  if (blockers.has("negative_person_claim")) return "Uppgift om person behöver kontrolleras";
  if (blockers.has("private_or_sensitive")) return "Känslig information behöver kontrolleras";
  if ([...blockers].some((x) => String(x).startsWith("publication_policy:"))) return "Publiceringsregeln behöver kontrolleras";
  return "Uppgiften behöver kontrolleras";
}

function formatWhen(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", { dateStyle: "short", timeStyle: "short" }).format(dt);
}

export default function WingmanBox({ onJumpToTab }) {
  const [overview, setOverview] = useState(null);
  const [wingman, setWingman] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewResult, wingmanResult] = await Promise.all([
        axios.get(`${API}/mod/editorial-overview`, { headers: modHeaders() }),
        axios.get(`${API}/mod/wingman`, { headers: modHeaders() }),
      ]);
      setOverview(overviewResult.data);
      setWingman(wingmanResult.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Kunde inte ladda redaktionens översikt");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseItems = useMemo(() => wingman?.items || [], [wingman]);
  const humanTasks = useMemo(() => baseItems.filter((item) => (
    HUMAN_CATEGORIES.has(item.category) && item.priority !== "low"
  )), [baseItems]);
  const backgroundTasks = useMemo(() => baseItems.filter((item) => !humanTasks.includes(item)), [baseItems, humanTasks]);
  const reviewItems = overview?.inbox || [];
  const inboxCount = reviewItems.length + humanTasks.length;

  const handlePublish = async (item, target) => {
    setBusy(item.fingerprint);
    try {
      await axios.post(
        `${API}/mod/editorial-review/${encodeURIComponent(item.fingerprint)}/publish`,
        { target },
        { headers: modHeaders() },
      );
      toast.success(target === "bygdepuls" ? "Publicerad i Bygdepuls" : "Publicerad som evenemang");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte publicera uppgiften");
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async (item) => {
    if (!window.confirm("Avvisa uppgiften? Källspåret sparas men inget publiceras.")) return;
    setBusy(item.fingerprint);
    try {
      await axios.post(
        `${API}/mod/editorial-review/${encodeURIComponent(item.fingerprint)}/reject`,
        {},
        { headers: modHeaders() },
      );
      toast.success("Uppgiften avvisades");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte avvisa uppgiften");
    } finally {
      setBusy(null);
    }
  };

  if (loading && !overview) {
    return <div className="bg-bone-alt/60 border border-warm-border p-4 mb-6 text-sm text-bark/70">Laddar redaktionell översikt…</div>;
  }
  if (error) {
    return (
      <div role="alert" className="bg-falu/10 border border-falu/30 text-falu p-4 mb-6 text-sm flex items-center gap-3">
        {error}
        <button onClick={load} className="ml-auto underline">Försök igen</button>
      </div>
    );
  }

  const automatic = overview?.automatic || { count: 0, recent: [] };
  const historical = overview?.historical || { count: 0, items: [] };
  const system = overview?.system || { overall: "unknown", parts: [] };
  const overallLabel = system.overall === "ok" ? "GRÖN" : system.overall === "error" ? "RÖD" : "GUL";
  const overallClass = system.overall === "ok" ? "text-pine" : system.overall === "error" ? "text-falu" : "text-amber-700";

  return (
    <div className="mb-7 space-y-3" data-testid="editorial-overview">
      <section className="border-2 border-warm-border bg-bone-alt/25">
        <div className="px-4 sm:px-5 py-4 flex items-center gap-3 border-b border-warm-border bg-bone-alt/55">
          <Inbox size={20} className="text-falu" />
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-semibold text-ink">Redaktionell inkorg</h2>
            <p className="text-xs text-bark/65">Bara sådant som behöver en mänsklig bedömning.</p>
          </div>
          <span className="ml-auto text-sm font-semibold text-ink">{inboxCount} att bedöma</span>
          <button onClick={load} aria-label="Uppdatera" className="p-1.5 text-bark/60 hover:text-ink">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {inboxCount === 0 ? (
          <div className="px-5 py-5 flex items-center gap-3 text-pine">
            <CheckCircle2 size={20} />
            <div>
              <p className="font-semibold">Inget behöver din bedömning just nu.</p>
              <p className="text-xs text-bark/65 mt-0.5">Systemmeddelanden och tekniskt skräp hanteras i bakgrunden.</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-warm-border/70">
            {reviewItems.map((item) => (
              <EditorialCard
                key={item.fingerprint}
                item={item}
                busy={busy === item.fingerprint}
                onPublish={handlePublish}
                onReject={handleReject}
              />
            ))}
            {humanTasks.map((item, index) => (
              <button
                key={`task-${index}`}
                onClick={() => onJumpToTab && onJumpToTab(item.tab, item)}
                className="w-full px-4 sm:px-5 py-4 flex items-start gap-3 text-left hover:bg-bone-alt/60"
              >
                <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-ink">{item.title}</p>
                  {item.subtitle && <p className="text-xs text-bark/70 mt-1">{item.subtitle}</p>}
                </div>
                <ChevronRight size={16} className="text-bark/45 mt-1" />
              </button>
            ))}
          </div>
        )}
      </section>

      <SimpleSection
        icon={ShieldCheck}
        title="Automatiskt hanterat"
        summary={`${automatic.count || 0} irrelevanta system- eller diagnosmeddelanden sorterade`}
        open={autoOpen}
        setOpen={setAutoOpen}
        tone="pine"
      >
        <p className="text-xs text-bark/70 mb-3">Dessa poster är inte raderade. Källspåret finns kvar, men de stör inte redaktionen.</p>
        {(automatic.recent || []).length === 0 ? (
          <p className="text-xs text-bark/60">Inga automatiskt sorterade poster ännu.</p>
        ) : (
          <ul className="space-y-2">
            {automatic.recent.map((item, i) => (
              <li key={i} className="text-xs text-bark/75 border-l-2 border-pine/30 pl-3">
                {item.text || "Facebook-systemmeddelande"}
                {formatWhen(item.closed_at) && <span className="block text-bark/50 mt-0.5">{formatWhen(item.closed_at)}</span>}
              </li>
            ))}
          </ul>
        )}
      </SimpleSection>

      <SimpleSection
        icon={Archive}
        title="Historiskt material"
        summary={`${historical.count || 0} äldre regionala uppgifter sparade`}
        open={historyOpen}
        setOpen={setHistoryOpen}
        tone="bark"
      >
        <p className="text-xs text-bark/70 mb-3">Äldre lokalt material sparas för det digitala minnet men visas inte som aktuella händelser.</p>
        {(historical.items || []).length === 0 ? (
          <p className="text-xs text-bark/60">Inget historiskt material har sorterats hit ännu.</p>
        ) : (
          <ul className="space-y-3">
            {historical.items.map((item) => (
              <li key={item.fingerprint} className="border-l-2 border-bark/20 pl-3">
                <p className="text-sm font-semibold text-ink">{item.title}</p>
                <p className="text-xs text-bark/65">Källa: {item.source_name}</p>
                {item.text && <p className="text-xs text-bark/75 mt-1 whitespace-pre-wrap">{item.text}</p>}
              </li>
            ))}
          </ul>
        )}
      </SimpleSection>

      <SimpleSection
        icon={Activity}
        title={`Systemstatus: ${overallLabel}`}
        summary={system.overall === "ok" ? "VIDALV arbetar normalt" : "Något behöver kontrolleras"}
        open={systemOpen}
        setOpen={setSystemOpen}
        tone={system.overall === "error" ? "falu" : system.overall === "ok" ? "pine" : "amber"}
        titleClass={overallClass}
      >
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {(system.parts || []).map((part) => {
            const state = STATUS[part.status] || STATUS.unknown;
            return (
              <div key={part.key} className="flex items-center gap-2 text-xs border border-warm-border/70 bg-bone-alt/30 px-3 py-2">
                <span className={`w-2 h-2 rounded-full ${state.dot}`} />
                <span className="font-medium text-ink">{part.label}</span>
                <span className="ml-auto text-bark/60">{state.text}</span>
              </div>
            );
          })}
        </div>
        <div className="border-t border-warm-border pt-3">
          <div className="flex items-center gap-2 text-xs text-bark/70">
            <Wrench size={14} />
            <span>{backgroundTasks.length} underhållspunkter finns i bakgrunden.</span>
          </div>
          {backgroundTasks.length > 0 && (
            <details className="mt-2 text-xs text-bark/70">
              <summary className="cursor-pointer hover:text-ink">Tekniska detaljer</summary>
              <ul className="mt-2 space-y-1 pl-4 list-disc">
                {backgroundTasks.map((item, i) => <li key={i}>{item.title}</li>)}
              </ul>
            </details>
          )}
        </div>
      </SimpleSection>
    </div>
  );
}

function EditorialCard({ item, busy, onPublish, onReject }) {
  return (
    <article className="px-4 sm:px-5 py-4 bg-white/20">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 w-2 h-2 rounded-full bg-falu shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-falu mb-1">{issueLabel(item)}</p>
          <h3 className="text-sm font-semibold text-ink">{item.title || item.source_name || "Uppgift att granska"}</h3>
          <p className="text-xs text-bark/65 mt-0.5">Källa: {item.source_name || "Okänd källa"}</p>
          {item.text && <p className="text-xs text-bark/80 mt-2 whitespace-pre-wrap leading-relaxed">{String(item.text).slice(0, 600)}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {(item.allowed_targets || []).map((target) => (
              <button
                key={target}
                disabled={busy}
                onClick={() => onPublish(item, target)}
                className="inline-flex items-center gap-1.5 bg-pine hover:bg-pine-hover text-white text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
              >
                <Check size={13} /> {target === "bygdepuls" ? "Publicera i Bygdepuls" : target === "evenemang" ? "Publicera som evenemang" : "Publicera"}
              </button>
            ))}
            {item.source_url && (
              <a href={item.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-pine hover:underline">
                <ExternalLink size={12} /> Öppna källan
              </a>
            )}
            <button
              disabled={busy}
              onClick={() => onReject(item)}
              className="inline-flex items-center gap-1.5 text-falu border border-falu/35 hover:bg-falu/10 text-xs font-semibold px-3 py-1.5 rounded disabled:opacity-50"
            >
              <X size={13} /> Avvisa
            </button>
          </div>
          <details className="mt-3 text-[11px] text-bark/55">
            <summary className="cursor-pointer inline-flex items-center gap-1"><Settings2 size={11} /> Tekniska detaljer</summary>
            <div className="mt-1.5 pl-4 space-y-0.5">
              <p>Klassificering: {item.classification || "none"}</p>
              <p>Blockeringar: {(item.review_blockers || []).join(", ") || "inga"}</p>
              {item.classifier_confidence != null && <p>Modellsäkerhet: {Math.round(Number(item.classifier_confidence) * 100)} %</p>}
            </div>
          </details>
        </div>
      </div>
    </article>
  );
}

function SimpleSection({ icon: Icon, title, summary, open, setOpen, children, tone, titleClass = "text-ink" }) {
  const iconClass = tone === "falu" ? "text-falu" : tone === "amber" ? "text-amber-700" : tone === "pine" ? "text-pine" : "text-bark";
  return (
    <section className="border border-warm-border bg-bone-alt/20">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 sm:px-5 py-3 flex items-center gap-3 text-left hover:bg-bone-alt/45"
        aria-expanded={open}
      >
        <Icon size={18} className={iconClass} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${titleClass}`}>{title}</p>
          <p className="text-xs text-bark/65 mt-0.5">{summary}</p>
        </div>
        {open ? <ChevronDown size={16} className="text-bark/50" /> : <ChevronRight size={16} className="text-bark/50" />}
      </button>
      {open && <div className="px-5 pb-4 pt-1 border-t border-warm-border/60">{children}</div>}
    </section>
  );
}
